import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { sectionRenderer, buildSectionSelector } from '@theme/section-renderer';
import { DrawerCloseEvent, DrawerOpenEvent } from '@theme/theme-drawer';

/** Shopify returns at most 10 product recommendations per request. */
const MAX_RECOMMENDATIONS = 10;
const DEFAULT_LIMIT = 3;
const DEFAULT_SECTION_ID = 'cart-upsell';
const OBSERVED_ATTRIBUTES = ['data-product-id', 'data-exclude', 'data-limit'];
const FOCUSABLE_SELECTOR =
  "summary, a[href]:not([tabindex^='-']), button:enabled, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex^='-'])";

/**
 * @typedef {object} UpsellState
 * @property {string} productId - The product that recommendations are based on.
 * @property {number} limit - The maximum number of rows to show.
 * @property {Set<string>} excluded - Product IDs that are already in the cart.
 * @property {string} key - Identifies this state, so unchanged re-renders are skipped.
 */

/**
 * Cart drawer upsell ("You may also like").
 *
 * The cart drawer re-renders through a hydration morph on every cart change. This element is
 * server-rendered empty with `data-skip-subtree-update` (see `snippets/cart-upsell.liquid`), so a morph
 * only refreshes its data attributes and never touches the rows inserted here. When those attributes
 * change, the component fetches Shopify product recommendations for the source product through the
 * Section Rendering API (`sections/cart-upsell.liquid`), drops products already in the cart, trims the
 * list to the merchant's limit, and morphs the result into place.
 *
 * Requests wait until the drawer is open, and a newer cart state aborts the request in flight. The
 * rows use the theme's `product-form-component`, so adding a product follows the regular cart path:
 * `CartLinesUpdateEvent`, drawer re-render, and cart count update.
 *
 * The element sits between the line items and the summary. When the summary is too tall to pin
 * (`cart-summary-sticky="false"` on the dialog, set by assets/cart-drawer.js on short screens), rows that
 * arrive while the drawer is open would push the Check out button down. In that layout, a response that
 * adds rows is held back and inserted after the drawer closes, ready for the next open.
 *
 * @extends {Component}
 */
class CartUpsellComponent extends Component {
  /** @type {AbortController | null} */
  #lifecycleController = null;

  /** @type {AbortController | null} */
  #requestController = null;

  /** Key of the state the current rows were rendered for. */
  #renderedKey = '';

  /** Key of the state the in-flight request is loading. */
  #pendingKey = '';

  /**
   * Rows held back while the open drawer cannot take them without moving the checkout button.
   * @type {{ content: HTMLElement, state: UpsellState } | null}
   */
  #deferred = null;

  /**
   * Morph refreshes this element's data attributes after each cart change; watch them directly so the
   * refresh does not depend on how the morph reports updates.
   */
  #attributeObserver = new MutationObserver(() => this.#refresh());

  connectedCallback() {
    super.connectedCallback();

    this.#lifecycleController = new AbortController();
    const { signal } = this.#lifecycleController;
    const drawer = this.#drawer;

    this.#attributeObserver.observe(this, { attributes: true, attributeFilter: OBSERVED_ATTRIBUTES });

    if (drawer) {
      drawer.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen, { signal });
      // `theme-drawer:close` fires as the slide-out starts; the dialog's own `close` fires once it is hidden.
      drawer.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose, { signal });
      this.#dialog?.addEventListener('close', this.#handleDrawerClose, { signal });
    }

    this.#refresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#lifecycleController?.abort();
    this.#lifecycleController = null;
    this.#attributeObserver.disconnect();
    this.#cancelRequest();
  }

  /** @returns {HTMLElement | null} The drawer that contains this upsell, if any. */
  get #drawer() {
    return /** @type {HTMLElement | null} */ (this.closest('theme-drawer'));
  }

  /** @returns {HTMLDialogElement | null} The drawer dialog, which carries the `cart-summary-sticky` state. */
  get #dialog() {
    return this.#drawer ? this.closest('dialog') : null;
  }

  /** @returns {boolean} Whether the upsell can be seen, so loading it is worthwhile. */
  get #isActive() {
    const drawer = this.#drawer;
    return !drawer || drawer.hasAttribute('open');
  }

  /** @returns {HTMLElement | null} The rendered upsell content, if any. */
  get #content() {
    return /** @type {HTMLElement | null} */ (this.querySelector(':scope > [data-cart-upsell-content]'));
  }

  /**
   * @param {Event} event - The drawer open event.
   */
  #handleDrawerOpen = (event) => {
    if (event.target !== this.#drawer) return;

    this.#refresh();
  };

  /**
   * @param {Event} event - The drawer close event, or the dialog's native close event.
   */
  #handleDrawerClose = (event) => {
    if (event.target !== this.#drawer && event.target !== this.#dialog) return;

    this.#insertDeferred();
  };

  /**
   * Inserts held-back rows once the drawer is hidden, so they are in place for the next open.
   */
  #insertDeferred() {
    const deferred = this.#deferred;
    // Wait for the slide-out to finish, and keep waiting if the drawer was reopened meanwhile.
    if (!deferred || this.#isActive || this.#dialog?.open) return;

    this.#deferred = null;
    // A cart change since then clears the deferred rows; this guards against a missed one.
    if (deferred.state.key !== this.#readState().key) return;

    this.#insert(deferred.content);
    this.#renderedKey = deferred.state.key;
  }

  /** @returns {UpsellState} */
  #readState() {
    const productId = (this.dataset.productId ?? '').trim();
    const parsedLimit = Number.parseInt(this.dataset.limit ?? '', 10);
    const limit = Math.min(Math.max(Number.isNaN(parsedLimit) ? DEFAULT_LIMIT : parsedLimit, 1), MAX_RECOMMENDATIONS);
    const excluded = new Set(
      (this.dataset.exclude ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
    const key = `${productId}|${limit}|${[...excluded].sort().join(',')}`;

    return { productId, limit, excluded, key };
  }

  /**
   * Brings the rows in line with the current cart state. Safe to call repeatedly.
   */
  #refresh() {
    if (!this.isConnected) return;

    const state = this.#readState();
    if (state.key === this.#renderedKey || state.key === this.#pendingKey || state.key === this.#deferred?.state.key) {
      return;
    }

    if (!state.productId || !this.dataset.url) {
      this.#cancelRequest();
      this.#clear();
      this.#renderedKey = state.key;
      return;
    }

    if (!this.#isActive) {
      // The drawer is closed: keep what it holds consistent with the cart now, and fetch on open.
      this.#cancelRequest();
      this.#prune(state);
      return;
    }

    this.#load(state);
  }

  /**
   * Fetches recommendations for the given state and renders them.
   * @param {UpsellState} state
   */
  async #load(state) {
    this.#cancelRequest();

    const controller = new AbortController();
    this.#requestController = controller;
    this.#pendingKey = state.key;

    const sectionId = this.dataset.sectionId || DEFAULT_SECTION_ID;

    try {
      const url = new URL(this.dataset.url ?? '', window.location.origin);
      url.searchParams.set('product_id', state.productId);
      // Ask for extra products to make up for the ones that are already in the cart.
      url.searchParams.set('limit', String(Math.min(MAX_RECOMMENDATIONS, state.limit + state.excluded.size)));
      url.searchParams.set('intent', 'related');

      const html = await sectionRenderer.getSectionHTML(sectionId, false, url, controller.signal);
      if (controller.signal.aborted || !this.isConnected) return;

      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const section = fragment.getElementById(buildSectionSelector(sectionId));
      // An error page has no section wrapper; treat it like a failed request.
      if (!section) throw new Error('Cart upsell section missing from the response');

      this.#render(section, state);
    } catch (error) {
      if (controller.signal.aborted) return;

      // Recommendations are optional. Keep any rows that still apply and retry on the next open or cart change.
      this.#prune(state);
    } finally {
      if (this.#requestController === controller) {
        this.#requestController = null;
        this.#pendingKey = '';
      }
    }
  }

  #cancelRequest() {
    this.#requestController?.abort();
    this.#requestController = null;
    this.#pendingKey = '';
    this.#deferred = null;
  }

  /**
   * Renders the response rows, or holds them back while they would push the checkout button down.
   * @param {HTMLElement} section - The rendered section wrapper from the Section Rendering API response.
   * @param {UpsellState} state
   */
  #render(section, state) {
    const newContent = section.querySelector('[data-cart-upsell-content]');
    const rowCount = newContent instanceof HTMLElement ? filterItems(newContent, state) : 0;

    if (!(newContent instanceof HTMLElement) || rowCount === 0) {
      this.#clear();
      this.#renderedKey = state.key;
      return;
    }

    if (this.#wouldPushSummary(rowCount)) {
      // Keep the current rows that still apply, and swap in the new ones after the drawer closes.
      this.#prune(state);
      this.#deferred = { content: newContent, state };
      return;
    }

    this.#insert(newContent);
    this.#renderedKey = state.key;
  }

  /**
   * Whether showing `rowCount` rows now would move the checkout button while the shopper sees it.
   *
   * With a pinned summary, the line items scroll on their own and the button stays put. With an unpinned
   * summary, it follows this element in the drawer's only scroll area, so more rows push it down, often
   * below the fold on short phones. Replacing existing rows one for one keeps it in place.
   *
   * @param {number} rowCount - The number of rows in the new content.
   * @returns {boolean}
   */
  #wouldPushSummary(rowCount) {
    const dialog = this.#dialog;
    // A hidden drawer can take rows at any time. While it slides out, `open` is still true.
    if (!dialog?.open || dialog.getAttribute('cart-summary-sticky') !== 'false') return false;

    const currentCount = this.#content?.querySelectorAll('[data-cart-upsell-item]').length ?? 0;
    return rowCount > currentCount;
  }

  /**
   * @param {HTMLElement} newContent - Upsell content that is already filtered for the current cart.
   */
  #insert(newContent) {
    const hadFocus = this.contains(document.activeElement);
    const currentContent = this.#content;

    if (currentContent) {
      morph(currentContent, newContent, { ...MORPH_OPTIONS, childrenOnly: false });
    } else {
      this.replaceChildren(document.adoptNode(newContent));
    }

    if (hadFocus) this.#restoreFocus();
  }

  /**
   * Removes rows that no longer apply without fetching.
   * @param {UpsellState} state
   */
  #prune(state) {
    const content = this.#content;
    if (!content) return;

    const hadFocus = this.contains(document.activeElement);

    if (filterItems(content, state) === 0) {
      this.replaceChildren();
    }

    if (hadFocus) this.#restoreFocus();
  }

  #clear() {
    this.#deferred = null;
    if (!this.hasChildNodes()) return;

    const hadFocus = this.contains(document.activeElement);
    this.replaceChildren();
    if (hadFocus) this.#restoreFocus();
  }

  /**
   * Keeps keyboard and screen reader users oriented when the focused row disappears, for example
   * after adding that product: focus moves to the upsell heading, or to the next control in the drawer.
   */
  #restoreFocus() {
    if (this.contains(document.activeElement)) return;

    const heading = this.querySelector('[data-cart-upsell-heading]');
    if (heading instanceof HTMLElement) {
      heading.focus();
      return;
    }

    const container = this.closest('dialog') ?? document.body;
    for (const element of container.querySelectorAll(FOCUSABLE_SELECTOR)) {
      if (!(element instanceof HTMLElement) || this.contains(element)) continue;
      if (!(this.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;

      element.focus();
      return;
    }
  }
}

/**
 * Removes rows for products in the cart and rows beyond the limit.
 * @param {Element} root - The upsell content.
 * @param {UpsellState} state
 * @returns {number} The number of rows left.
 */
function filterItems(root, { excluded, limit }) {
  let count = 0;

  for (const item of root.querySelectorAll('[data-cart-upsell-item]')) {
    const productId = item instanceof HTMLElement ? item.dataset.productId : undefined;

    if (!productId || excluded.has(productId) || count >= limit) {
      item.remove();
      continue;
    }

    count += 1;
  }

  return count;
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
